import type { IRefundOffersByFulfillmentId } from "../../Orchestrator/Recoils/AddonsAtoms";
import { AdmissionStatusEnum, isShuttleOffer } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookingResponse } from "../../Orchestrator/Types/Index";
import { isNolAdmission } from "../../Lib/Utils/nolCard";
import { nolFulfillmentIds } from "./onwardRefund";

/**
 * A Nol pass follows its TRAIN TICKET.
 *
 * The pass has its own refund rules at the retailer and they are not the ones
 * that apply: on INT a Gold day pass quotes `refundFee 0 / refundableAmount
 * 4000` — a full AED 40 back — while the Saver ticket it rides on returns
 * nothing (booking AURHIL, 2026-09-18). The business rule is that the pass is
 * only refundable when the ticket is, so the pass's own quote is the wrong
 * question and asking it would offer a refund on every non-refundable fare.
 *
 * The verdict is PER RIDER. A leg can be mixed — a partial exchange leaves one
 * rider on a Flex admission and another on a Saver — and judging the leg as a
 * whole would let the Saver rider's pass through on the back of their
 * companion's refundable fare.
 */

/**
 * The FULFILLED train admissions covering this leg, optionally narrowed to one
 * rider.
 *
 * ONE walk for both scopes on purpose: the leg-wide and per-rider filters
 * differ by a single condition, and a one-sided edit to a money filter is
 * exactly what the rest of this module goes out of its way to prevent.
 *
 * Three exclusions, all load-bearing:
 *  - Nol passes themselves. Bought via add-fmlm-travel-passes a pass IS an
 *    admission on the same leg (`isFmLmTravelPass`), so an unfiltered walk would
 *    ask the pass about itself and the gate would never fire.
 *  - Shuttle offers. A shuttle is also carried as an admission and is its own
 *    product with its own rules; letting one stand in for the ticket would gate
 *    the pass on the wrong fare. `cdtShuttleRefundRows` depends on this
 *    exclusion holding.
 *  - Anything not FULFILLED. A REFUNDED or EXCHANGED admission is history.
 *
 * `passengerIds` is nullable on an admission (docs/retailer.json), so an
 * unattributed ticket names nobody and cannot block anybody — the fail-open
 * direction.
 */
export function ticketFulfillmentIdsForLeg(
  booking: IBookingResponse | null,
  legId: string,
  passengerId?: string
): string[] {
  if (!legId) return [];
  const ids: string[] = [];
  for (const offer of (booking?.booking?.bookedOffers || []) as any[]) {
    if (isShuttleOffer(offer)) continue;
    for (const adm of offer?.admissions || []) {
      if (!adm?.id || isNolAdmission(adm)) continue;
      if (adm?.status !== AdmissionStatusEnum.FULFILLED) continue;
      if (!(adm?.tripCoverage?.coveredLegIds || []).includes(legId)) continue;
      if (passengerId && !(adm?.passengerIds || []).includes(passengerId)) continue;
      if (!ids.includes(adm.id)) ids.push(adm.id);
    }
  }
  return ids;
}

/**
 * Ticket ids worth quoting speculatively: only the legs that actually hold a
 * FULFILLED Nol pass.
 *
 * Narrow on purpose. The probe this feeds is shared with manage-seating, and a
 * booking with no pass must keep making exactly the request it made before.
 */
export function ticketFulfillmentIdsForNolLegs(
  booking: IBookingResponse | null
): string[] {
  const seen = new Set<string>();
  for (const trip of (booking?.booking?.trips || []) as any[]) {
    for (const leg of trip?.legs || []) {
      if (!leg?.id) continue;
      if (nolFulfillmentIds(booking, leg.id).length === 0) continue;
      for (const id of ticketFulfillmentIdsForLeg(booking, leg.id)) seen.add(id);
    }
  }
  return [...seen];
}

/**
 * Does this exact set of ticket ids PROVE non-refundability?
 *
 * `priced > 0` separates the two ways a ticket reads as zero. A non-refundable
 * fare is `refundFee 2400 / refundableAmount 0` — the fee ate the fare, which is
 * evidence. `0 / 0` is a part the backend priced at nothing at all, and
 * ProbeRefundOffersAction's even-split fallback can manufacture it from a zero
 * aggregate. Treating that as "non-refundable" would block on absence of
 * evidence, which is the one thing this module exists to prevent.
 *
 * An empty set is likewise no evidence, never a block.
 *
 * Keyed on the ADMISSION id, which is what the breakdown branch of
 * ProbeRefundOffersAction stamps (`bookingParts[].id`). Its even-split fallback
 * keys on `fulfillments[].id` instead, and the two are not the same namespace in
 * general (see TicketsModal). That mismatch can only leave a ticket unstamped —
 * no entry, no block — so it fails open, quietly disabling the gate on an
 * aggregate-only response rather than deciding wrongly.
 */
function ticketSetBlocks(
  quotes: IRefundOffersByFulfillmentId,
  ticketIds: string[]
): boolean {
  if (ticketIds.length === 0) return false;
  let refundable = 0;
  let priced = 0;
  for (const id of ticketIds) {
    const entry = quotes.entries?.[id];
    // Priced nothing about this ticket — so say nothing about the pass.
    if (!entry || typeof entry.refundableAmount !== "number") return false;
    refundable += entry.refundableAmount;
    priced += entry.refundableAmount + (entry.refundFee || 0);
  }
  return priced > 0 && refundable === 0;
}

/** A usable quote for THIS booking, or null. */
function quotesFor(
  quotes: IRefundOffersByFulfillmentId | null,
  booking: IBookingResponse | null
): IRefundOffersByFulfillmentId | null {
  const bookingId = booking?.booking?.id;
  if (!quotes || !bookingId || quotes.bookingId !== bookingId) return null;
  return quotes;
}

/**
 * Is EVERY ticket on the leg non-refundable?
 *
 * The fallback verdict, used only when no pass on the leg can be attributed to a
 * rider — `passengersBlockedByTicket` has nobody to ask, and a leg-wide sum is
 * then the only evidence available. When riders CAN be named, the per-rider
 * verdict governs; see `NolCardModal`, where using this as an independent hide
 * gate produced a visible, enabled Refund button with an empty roster behind it.
 */
export function nolRefundBlockedByTicket(
  quotes: IRefundOffersByFulfillmentId | null,
  booking: IBookingResponse | null,
  legId: string
): boolean {
  const usable = quotesFor(quotes, booking);
  if (!usable) return false;
  return ticketSetBlocks(usable, ticketFulfillmentIdsForLeg(booking, legId));
}

/**
 * Which of these riders may NOT have their pass refunded, because their OWN
 * ticket returns nothing.
 *
 * A rider with no ticket of their own on this leg is NOT blocked — absence of
 * evidence again.
 */
export function passengersBlockedByTicket(
  quotes: IRefundOffersByFulfillmentId | null,
  booking: IBookingResponse | null,
  legId: string,
  passengerIds: string[]
): Set<string> {
  const blocked = new Set<string>();
  const usable = quotesFor(quotes, booking);
  if (!usable || !legId) return blocked;
  for (const passengerId of passengerIds) {
    if (ticketSetBlocks(usable, ticketFulfillmentIdsForLeg(booking, legId, passengerId))) {
      blocked.add(passengerId);
    }
  }
  return blocked;
}

/**
 * Is any pass on this leg shared between a rider who may refund and one who may
 * not?
 *
 * `passBelongsToPassenger` (nolCard.ts) keeps a pass if ANY named rider is in
 * scope — deliberately, so a scoped refund never drops a pass it cannot prove is
 * somebody else's. The consequence here is the opposite hazard: refunding the
 * allowed rider would cancel the blocked rider's travel right on the same
 * fulfillment. The two cannot both be honoured, and picking one silently is what
 * ACPT-006 was.
 *
 * `shuttleRefundablePassengerIds` records the same assumption for its product
 * ("ASSUMES ONE ADMISSION PER RIDER… if that shape ever appears, the rows have
 * to be grouped and locked together"). This is the Nol side of it, detected
 * rather than assumed.
 */
export function nolPassSharedAcrossBlockedRiders(
  booking: IBookingResponse | null,
  legId: string,
  blocked: ReadonlySet<string>,
  allowed: readonly string[]
): boolean {
  if (blocked.size === 0 || allowed.length === 0) return false;
  const legPassIds = new Set(nolFulfillmentIds(booking, legId));
  if (legPassIds.size === 0) return false;
  const allowedSet = new Set(allowed);
  for (const offer of (booking?.booking?.bookedOffers || []) as any[]) {
    for (const pass of [...(offer?.ancillaries || []), ...(offer?.admissions || [])]) {
      if (!pass?.id || !legPassIds.has(pass.id)) continue;
      const riders: string[] = pass?.passengerIds || [];
      if (riders.some((id) => allowedSet.has(id)) && riders.some((id) => blocked.has(id))) {
        return true;
      }
    }
  }
  return false;
}
